import { Dialog, DialogTitle, DialogContent, DialogActions, IconButton } from "@mui/material";
import { styled } from "@mui/material/styles";
import { DIALOG_ANIMATIONS } from "./utils/constants";

/**
 * BootstrapDialog - Enhanced base dialog with consistent spacing and typography
 * Features: Custom padding, improved title styling, standardized component spacing
 */
export const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: theme.spacing(2) // 16px padding for content area
  },
  "& .MuiDialogActions-root": {
    padding: theme.spacing(1) // 8px padding for action buttons area
  },
  "& .MuiDialogTitle-root": {
    fontSize: "1.25rem", // 20px font size for better hierarchy
    fontWeight: 600, // Semi-bold weight for emphasis
    color: theme.palette.text.primary // Primary text color for consistency
  }
}));

/**
 * StyledDialogTitle - Enhanced dialog header with flex layout and visual separation
 * Features: Space-between layout, bottom border separator, relative positioning for close button
 */
export const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  display: "flex", // Flexbox layout for title and close button
  justifyContent: "space-between", // Spreads title and close button to opposite ends
  alignItems: "center", // Vertically centers content
  borderBottom: `1px solid ${theme.palette.divider}`, // Visual separator line
  backgroundColor: theme.palette.background.paper, // Clean background color
  position: "relative" // Allows absolute positioning of child elements
}));

/**
 * StyledDialogContent - Main content area with proper spacing and divider support
 * Features: Generous padding, minimum height, optional border separators when dividers enabled
 */
export const StyledDialogContent = styled(DialogContent)(({ theme }) => ({
  padding: theme.spacing(3), // 24px padding for comfortable content spacing
  minHeight: "100px", // Prevents dialog from being too small
  "&.MuiDialogContent-dividers": {
    // When dividers prop is enabled
    borderTop: `1px solid ${theme.palette.divider}`, // Top border separator
    borderBottom: `1px solid ${theme.palette.divider}` // Bottom border separator
  }
}));

/**
 * StyledDialogActions - Enhanced action area with button layout and visual separation
 * Features: Button gaps, background differentiation, top border separator, proper padding
 */
export const StyledDialogActions = styled(DialogActions)(({ theme }) => ({
  padding: theme.spacing(2), // 16px padding around action buttons
  gap: theme.spacing(1), // 8px gap between action buttons
  backgroundColor: theme.palette.background.default, // Subtle background difference
  borderTop: `1px solid ${theme.palette.divider}` // Visual separator from content
}));

/**
 * CloseIconButton - Enhanced close button with hover effects and absolute positioning
 * Features: Top-right positioning, smooth transitions, hover state styling, consistent appearance
 */
export const CloseIconButton = styled(IconButton)(({ theme }) => ({
  position: "absolute", // Positioned relative to dialog title
  right: 8, // 8px from right edge
  top: 8, // 8px from top edge
  color: theme.palette.grey[500], // Subtle gray color for non-intrusive appearance
  "&:hover": {
    // Hover state styling
    backgroundColor: theme.palette.action.hover, // Light background on hover
    color: theme.palette.grey[700] // Darker color on hover for better visibility
  },
  transition: theme.transitions.create(["background-color", "color"], {
    duration: theme.transitions.duration.short // Smooth 250ms transition
  })
}));

/**
 * AnimatedDialog - Advanced dialog with custom entrance/exit animations
 * Features: DIALOG_ANIMATIONS integration, smooth scale/opacity transitions, separate entrance/exit timing
 */
export const AnimatedDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialog-paper": {
    // Entrance animation configuration
    transition: theme.transitions.create(["opacity", "transform"], {
      duration: DIALOG_ANIMATIONS.ENTER_DURATION, // 300ms entrance duration
      easing: theme.transitions.easing.easeOut // Smooth deceleration curve
    }),
    // Ensure dialog appears at full scale and opacity when visible
    "&.MuiDialog-paperScrollPaper": {
      transform: "scale(1)", // Full size (no scaling)
      opacity: 1 // Fully opaque
    }
  },
  "& .MuiBackdrop-root": {
    // Backdrop fade animation with consistent timing
    transition: theme.transitions.create("opacity", {
      duration: DIALOG_ANIMATIONS.ENTER_DURATION // 300ms backdrop fade
    })
  },
  // Exit animation configuration (when dialog is closing)
  "&.MuiDialog-root": {
    "& .MuiDialog-paper": {
      transition: theme.transitions.create(["opacity", "transform"], {
        duration: DIALOG_ANIMATIONS.EXIT_DURATION, // 200ms exit duration (faster than entrance)
        easing: theme.transitions.easing.sharp // Sharp acceleration for quick exit
      })
    }
  }
}));
